import React, { useState, useEffect, useCallback } from 'react';
import { useParams, Link } from 'react-router-dom';
import {
  CheckCircle, AlertTriangle, XCircle, Loader2, ArrowLeft,
  Calendar, MapPin, ScanLine, Shield
} from 'lucide-react';
import axios from 'axios';
import { useAuth } from '../context/AuthContext';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

const CheckInPage = () => {
  const { entryId } = useParams();
  const { user, isAdmin } = useAuth();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [checkingIn, setCheckingIn] = useState(false);
  const [checkinResult, setCheckinResult] = useState(null);

  const fetchEntry = useCallback(async () => {
    try {
      const res = await axios.get(`${BACKEND_URL}/api/public/checkin/${entryId}`);
      setData(res.data);
    } catch {
      setData({ status: 'invalid', message: 'Entry ID not found' });
    }
    setLoading(false);
  }, [entryId]);

  useEffect(() => { fetchEntry(); }, [fetchEntry]);

  const handleCheckIn = async () => {
    if (checkingIn) return;
    setCheckingIn(true);
    try {
      const res = await axios.post(`${BACKEND_URL}/api/admin/events/checkin-scan`,
        { entry_id: entryId }, { withCredentials: true });
      setCheckinResult(res.data);
      // Vibrate on success
      if (navigator.vibrate) navigator.vibrate(200);
    } catch (err) {
      setCheckinResult({
        status: 'error',
        message: err.response?.data?.detail || 'Check-in failed. Are you logged in as admin?'
      });
    }
    setCheckingIn(false);
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-[#0A0A0A] flex items-center justify-center">
        <Loader2 className="w-10 h-10 animate-spin text-white" />
      </div>
    );
  }

  // After admin performs check-in
  if (checkinResult) {
    const isSuccess = checkinResult.status === 'checked_in';
    const isWarning = checkinResult.status === 'already_checked_in';
    const bgColor = isSuccess ? 'bg-green-600' : isWarning ? 'bg-amber-500' : 'bg-red-600';
    const Icon = isSuccess ? CheckCircle : isWarning ? AlertTriangle : XCircle;

    return (
      <div className={`min-h-screen ${bgColor} flex flex-col items-center justify-center px-6 text-center`}
        data-testid="checkin-result-page">
        <Icon className="w-24 h-24 text-white mb-6" strokeWidth={1.5} />
        <h1 className="text-4xl font-bold text-white mb-3">
          {isSuccess ? 'Checked In!' : isWarning ? 'Already Checked In' : 'Error'}
        </h1>
        {checkinResult.attendee_name && (
          <p className="text-2xl text-white/90 font-semibold mb-2">{checkinResult.attendee_name}</p>
        )}
        <p className="text-lg text-white/80 mb-2">{checkinResult.message}</p>
        <p className="text-white/60 font-mono text-sm mb-8">{entryId}</p>
        <button onClick={() => { setCheckinResult(null); fetchEntry(); }}
          className="bg-white/20 text-white px-6 py-3 rounded-xl text-sm font-semibold hover:bg-white/30 transition-colors"
          data-testid="scan-next-btn">
          Scan Next Guest
        </button>
      </div>
    );
  }

  // Invalid Entry
  if (data.status === 'invalid') {
    return (
      <div className="min-h-screen bg-red-600 flex flex-col items-center justify-center px-6 text-center"
        data-testid="checkin-invalid">
        <XCircle className="w-24 h-24 text-white mb-6" strokeWidth={1.5} />
        <h1 className="text-4xl font-bold text-white mb-3">Invalid Entry</h1>
        <p className="text-lg text-white/80 mb-2">This Entry ID was not found in the system.</p>
        <p className="text-white/60 font-mono text-sm mb-8">{entryId}</p>
        <Link to="/events" className="bg-white/20 text-white px-6 py-3 rounded-xl text-sm font-semibold hover:bg-white/30">
          <ArrowLeft className="w-4 h-4 inline mr-2" />Back to Events
        </Link>
      </div>
    );
  }

  // Already checked in (viewed by anyone)
  if (data.status === 'already_checked_in') {
    return (
      <div className="min-h-screen bg-amber-500 flex flex-col items-center justify-center px-6 text-center"
        data-testid="checkin-already">
        <AlertTriangle className="w-24 h-24 text-white mb-6" strokeWidth={1.5} />
        <h1 className="text-4xl font-bold text-white mb-3">Already Checked In</h1>
        <p className="text-2xl text-white/90 font-semibold mb-2">{data.attendee_name}</p>
        <p className="text-lg text-white/70 mb-1">{data.event_title}</p>
        {data.checked_in_at && (
          <p className="text-sm text-white/60 mb-2">Checked in at: {new Date(data.checked_in_at).toLocaleTimeString()}</p>
        )}
        <p className="text-white/60 font-mono text-sm mb-8">{entryId}</p>
        {isAdmin && (
          <Link to="/admin" className="bg-white/20 text-white px-6 py-3 rounded-xl text-sm font-semibold hover:bg-white/30">
            Back to Admin
          </Link>
        )}
      </div>
    );
  }

  // Valid entry — show info + check-in button for admin
  return (
    <div className="min-h-screen bg-[#0A0A0A] flex flex-col items-center justify-center px-6 text-center"
      data-testid="checkin-valid">
      <ScanLine className="w-16 h-16 text-[#BA1826] mb-6" />
      <h1 className="text-3xl font-bold text-white mb-1">{data.attendee_name}</h1>
      <p className="font-mono text-lg text-gray-400 mb-6">{entryId}</p>

      <div className="bg-white/10 rounded-xl p-5 mb-8 w-full max-w-sm">
        {data.event_title && (
          <div className="flex items-center gap-2 text-gray-300 text-sm mb-2">
            <Calendar className="w-4 h-4 text-[#BA1826]" />
            <span>{data.event_title}</span>
          </div>
        )}
        {data.event_date && (
          <div className="flex items-center gap-2 text-gray-400 text-sm mb-2">
            <Calendar className="w-4 h-4" />
            <span>{data.event_date}</span>
          </div>
        )}
        {data.event_location && (
          <div className="flex items-center gap-2 text-gray-400 text-sm">
            <MapPin className="w-4 h-4" />
            <span>{data.event_location}</span>
          </div>
        )}
      </div>

      {isAdmin ? (
        <button onClick={handleCheckIn} disabled={checkingIn}
          className="bg-green-600 hover:bg-green-700 text-white px-10 py-4 rounded-xl text-lg font-bold transition-colors disabled:opacity-50 flex items-center gap-3"
          data-testid="confirm-checkin-btn">
          {checkingIn ? <Loader2 className="w-6 h-6 animate-spin" /> : <CheckCircle className="w-6 h-6" />}
          {checkingIn ? 'Checking In...' : 'Check In This Guest'}
        </button>
      ) : (
        <div className="bg-white/10 rounded-xl p-5 w-full max-w-sm">
          <Shield className="w-8 h-8 text-gray-500 mx-auto mb-2" />
          <p className="text-gray-400 text-sm">This entry pass is valid. Show this to staff at the door for check-in.</p>
          <Link to="/admin/login" className="text-xs text-[#BA1826] mt-3 inline-block hover:underline">
            Staff login
          </Link>
        </div>
      )}
    </div>
  );
};

export default CheckInPage;
